import React, { useEffect, useRef, useState } from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import ViewColumnIcon from '@mui/icons-material/ViewColumnOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import { SavedView } from 'api/ExportHubAPI';
import FacetSelect from './FacetSelect';
import SaveViewModal from './SaveViewModal';
import { STATUS_OPTIONS, JURISDICTION_OPTIONS, TIER_OPTIONS } from './productPresets';
import {
  ProductFilters,
  FilterFacetKey,
  PRODUCT_COLUMN_DEFS,
  orderColumns,
  countActiveFilters,
  EMPTY_FILTERS,
} from './productFilters';

interface FacetOptionData {
  origins: string[];
  destinations: string[];
  htsChapters: string[];
}

interface Props {
  filters: ProductFilters;
  onFiltersChange: (_f: ProductFilters) => void;
  facetOptions: FacetOptionData;
  // Cross-database Products Overview only: the Database facet (id→name).
  databaseOptions?: Array<{ id: string; name: string }>;
  visibleColumns: string[];
  onColumnsChange: (_cols: string[]) => void;
  totalCount: number;
  filteredCount: number;
  savedViews: SavedView[];
  activeViewId: string | null;
  onApplyView: (_v: SavedView) => void;
  onSaveView: (_name: string) => Promise<void>;
  onPinView: (_v: SavedView) => void;
  onRenameView: (_v: SavedView, _name: string) => void;
  onDeleteView: (_v: SavedView) => void;
  savingView: boolean;
}

const FACET_LABELS: Record<FilterFacetKey, string> = {
  status: 'Compliance',
  jurisdiction: 'Jurisdiction',
  tier: 'Tier',
  origin: 'Origin',
  destination: 'Destination',
  htsChapter: 'HTS Chapter',
};

const COLUMN_OPTIONS = PRODUCT_COLUMN_DEFS.filter((c) => c.toggleable).map(
  (c) => ({ value: c.key, label: c.label }),
);

const SavedViewChip: React.FC<{
  view: SavedView;
  active: boolean;
  onApply: () => void;
  onPin: () => void;
  onRename: (_name: string) => void;
  onDelete: () => void;
}> = ({ view, active, onApply, onPin, onRename, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(view.name);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  if (renaming) {
    return (
      <span className='eh-view-chip renaming'>
        <input
          className='eh-view-rename-input'
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft.trim()) {
              onRename(draft.trim());
              setRenaming(false);
            } else if (e.key === 'Escape') {
              setDraft(view.name);
              setRenaming(false);
            }
          }}
          onBlur={() => {
            if (draft.trim() && draft.trim() !== view.name) onRename(draft.trim());
            setRenaming(false);
          }}
        />
      </span>
    );
  }

  return (
    <span className={`eh-view-chip ${active ? 'active' : ''}`} ref={ref}>
      <button type='button' className='eh-view-apply' onClick={onApply}>
        {view.is_pinned && (
          <PushPinIcon style={{ fontSize: 12, marginRight: 4 }} />
        )}
        {view.name}
      </button>
      <button
        type='button'
        className='eh-view-kebab'
        onClick={() => setMenuOpen((o) => !o)}
        aria-label='View actions'
      >
        <MoreVertIcon style={{ fontSize: 15 }} />
      </button>
      {menuOpen && (
        <div className='eh-facet-menu align-right'>
          <button
            type='button'
            className='eh-facet-option'
            onClick={() => {
              onPin();
              setMenuOpen(false);
            }}
          >
            {view.is_pinned ? (
              <PushPinOutlinedIcon style={{ fontSize: 15 }} />
            ) : (
              <PushPinIcon style={{ fontSize: 15 }} />
            )}
            {view.is_pinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            type='button'
            className='eh-facet-option'
            onClick={() => {
              setDraft(view.name);
              setRenaming(true);
              setMenuOpen(false);
            }}
          >
            Rename
          </button>
          <button
            type='button'
            className='eh-facet-option eh-facet-danger'
            onClick={() => {
              onDelete();
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      )}
    </span>
  );
};

const ProductFilterBar: React.FC<Props> = ({
  filters,
  onFiltersChange,
  facetOptions,
  databaseOptions,
  visibleColumns,
  onColumnsChange,
  totalCount,
  filteredCount,
  savedViews,
  activeViewId,
  onApplyView,
  onSaveView,
  onPinView,
  onRenameView,
  onDeleteView,
  savingView,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const setFacet = (key: FilterFacetKey, next: string[]) =>
    onFiltersChange({ ...filters, [key]: next });

  const selectedColumns = visibleColumns.filter((k) =>
    COLUMN_OPTIONS.some((c) => c.value === k),
  );

  const activeCount = countActiveFilters(filters);
  const moreCount =
    filters.origin.length +
    filters.destination.length +
    filters.htsChapter.length +
    (filters.needsJurisdiction ? 1 : 0);

  const handleSave = async (name: string) => {
    await onSaveView(name);
    setSaveOpen(false);
  };

  return (
    <div className='eh-filterbar'>
      {/* Saved views row */}
      <div className='eh-views-row'>
        <span className='eh-views-label'>Saved views</span>
        <div className='eh-views-list'>
          {savedViews.length === 0 && (
            <span className='eh-views-empty'>None yet</span>
          )}
          {savedViews.map((v) => (
            <SavedViewChip
              key={v.id}
              view={v}
              active={v.id === activeViewId}
              onApply={() => onApplyView(v)}
              onPin={() => onPinView(v)}
              onRename={(name) => onRenameView(v, name)}
              onDelete={() => onDeleteView(v)}
            />
          ))}
        </div>
        <button
          type='button'
          className='eh-save-view-btn'
          onClick={() => setSaveOpen(true)}
          disabled={activeCount === 0}
          title={
            activeCount === 0
              ? 'Apply filters first to save a view'
              : 'Save current view'
          }
        >
          <BookmarkAddOutlinedIcon style={{ fontSize: 16 }} /> Save current view
        </button>
      </div>

      {/* Primary facet row */}
      <div className='eh-facet-row'>
        {databaseOptions && (
          <FacetSelect
            label='Database'
            options={databaseOptions.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            selected={filters.database}
            onChange={(n) => onFiltersChange({ ...filters, database: n })}
          />
        )}
        <FacetSelect
          label='Compliance'
          options={STATUS_OPTIONS}
          selected={filters.status}
          onChange={(n) => setFacet('status', n)}
        />
        <FacetSelect
          label='Jurisdiction'
          options={JURISDICTION_OPTIONS}
          selected={filters.jurisdiction}
          onChange={(n) => setFacet('jurisdiction', n)}
        />
        <FacetSelect
          label='Tier'
          options={TIER_OPTIONS}
          selected={filters.tier}
          onChange={(n) => setFacet('tier', n)}
        />
        <button
          type='button'
          className={`eh-facet-btn ${moreCount ? 'active' : ''}`}
          onClick={() => setMoreOpen((o) => !o)}
        >
          <TuneIcon style={{ fontSize: 16 }} /> More filters
          {moreCount > 0 && <span className='eh-facet-count'>{moreCount}</span>}
        </button>

        <div className='eh-facet-spacer' />

        <FacetSelect
          label='Columns'
          startIcon={<ViewColumnIcon style={{ fontSize: 16 }} />}
          options={COLUMN_OPTIONS}
          selected={selectedColumns}
          onChange={(n) => onColumnsChange(orderColumns(['name', ...n]))}
          align='right'
        />
      </div>

      {/* Expandable secondary facets */}
      {moreOpen && (
        <div className='eh-facet-row eh-facet-row-more'>
          <FacetSelect
            label='Origin'
            options={facetOptions.origins}
            selected={filters.origin}
            onChange={(n) => setFacet('origin', n)}
          />
          <FacetSelect
            label='Destination'
            options={facetOptions.destinations}
            selected={filters.destination}
            onChange={(n) => setFacet('destination', n)}
          />
          <FacetSelect
            label='HTS Chapter'
            options={facetOptions.htsChapters}
            selected={filters.htsChapter}
            onChange={(n) => setFacet('htsChapter', n)}
          />
          <button
            type='button'
            className={`eh-facet-toggle ${
              filters.needsJurisdiction ? 'active' : ''
            }`}
            onClick={() =>
              onFiltersChange({
                ...filters,
                needsJurisdiction: !filters.needsJurisdiction,
              })
            }
            aria-pressed={filters.needsJurisdiction}
          >
            <span className='eh-facet-check'>
              {filters.needsJurisdiction && '✓'}
            </span>
            Needs Jurisdiction
          </button>
        </div>
      )}

      {/* Active chips + summary */}
      <div className='eh-active-row'>
        <span className='eh-active-summary'>
          {activeCount > 0
            ? `${filteredCount.toLocaleString()} of ${totalCount.toLocaleString()} products`
            : `${totalCount.toLocaleString()} products`}
        </span>
        {(Object.keys(FACET_LABELS) as FilterFacetKey[]).flatMap((key) =>
          filters[key].map((val) => (
            <span key={`${key}:${val}`} className='eh-active-chip'>
              <span className='eh-active-chip-key'>{FACET_LABELS[key]}:</span>
              {val}
              <button
                type='button'
                className='eh-active-chip-x'
                onClick={() =>
                  setFacet(
                    key,
                    filters[key].filter((v) => v !== val),
                  )
                }
                aria-label={`Remove ${val}`}
              >
                <CloseIcon style={{ fontSize: 13 }} />
              </button>
            </span>
          )),
        )}
        {filters.database.map((id) => {
          const db = databaseOptions?.find((d) => d.id === id);
          return (
            <span key={`db:${id}`} className='eh-active-chip'>
              <span className='eh-active-chip-key'>Database:</span>
              {db?.name || id}
              <button
                type='button'
                className='eh-active-chip-x'
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    database: filters.database.filter((x) => x !== id),
                  })
                }
                aria-label='Remove database filter'
              >
                <CloseIcon style={{ fontSize: 13 }} />
              </button>
            </span>
          );
        })}
        {filters.needsJurisdiction && (
          <span className='eh-active-chip'>
            <span className='eh-active-chip-key'>Flag:</span>
            Needs Jurisdiction
            <button
              type='button'
              className='eh-active-chip-x'
              onClick={() =>
                onFiltersChange({ ...filters, needsJurisdiction: false })
              }
              aria-label='Remove Needs Jurisdiction filter'
            >
              <CloseIcon style={{ fontSize: 13 }} />
            </button>
          </span>
        )}
        {activeCount > 0 && (
          <button
            type='button'
            className='eh-clear-all'
            onClick={() => onFiltersChange({ ...EMPTY_FILTERS })}
          >
            Clear all
          </button>
        )}
      </div>

      <SaveViewModal
        open={saveOpen}
        filters={filters}
        columnCount={selectedColumns.length}
        saving={savingView}
        onClose={() => setSaveOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
};

export default ProductFilterBar;
